import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { audioUrl, AUDIO_BASE_PATH } from "./library";

// ── The music player (Phase 3) ────────────────────────────────────────────
//
// The audio element lives HERE, in the app shell, ABOVE the routing. That is
// the whole point: navigation between screens must never stop the music. The
// /music screen only tells the player WHAT to play; the player owns the audio.
//
// The player is deliberately dumb about the truth: it plays the URL the
// catalog gives it and reports what the browser said. If the file cannot be
// reached (the audio lives on Kit's Mac; the deployed backend mounts it in a
// later phase), the player reports "local only" and the UI says so plainly —
// it never pretends to be playing.
//
// iOS: the first play MUST come from a user gesture. Every play path here
// (the screen's play buttons, the bar, the media keys) is a gesture, and the
// first one also unlocks the element.

const MusicPlayerContext = createContext(null);

// The context's value when there is no provider (the bare /music/board wall).
// Every call is a no-op and `available` says why.
const NO_PLAYER = {
  available: false,
  queue: [],
  index: -1,
  currentTrack: null,
  playing: false,
  currentTime: 0,
  duration: 0,
  volume: 0.8,
  status: "idle",
  isLocalOnly: false,
  queueOpen: false,
  playTrack: () => {},
  playPlaylist: () => {},
  toggle: () => {},
  play: () => {},
  pause: () => {},
  next: () => {},
  prev: () => {},
  seek: () => {},
  seekBy: () => {},
  setVolume: () => {},
  toggleQueue: () => {},
};

export const useMusicPlayer = () => useContext(MusicPlayerContext) || NO_PLAYER;

const safePlay = (el) => {
  try {
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch {
    /* a browser that refuses to play (no user gesture yet) is not an error */
  }
};

const safeLoad = (el) => {
  try {
    el.load();
  } catch {
    /* jsdom and some mobile browsers do not implement load() */
  }
};

export const MusicPlayerProvider = ({
  children,
  basePath = AUDIO_BASE_PATH,
}) => {
  const audioRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.8);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | unavailable
  const [queueOpen, setQueueOpen] = useState(false);

  const currentTrack = index >= 0 ? queue[index] || null : null;

  // Refs for the handlers the audio element and MediaSession need, so the
  // listeners are attached once and never go stale.
  const nextRef = useRef(() => {});
  const prevRef = useRef(() => {});
  const playRef = useRef(() => {});
  const pauseRef = useRef(() => {});

  // ── loading a track into the element ──
  const loadTrack = useCallback(
    (track, autoplay) => {
      const el = audioRef.current;
      if (!el || !track) return;
      const url = audioUrl(track, basePath);
      if (!url) {
        // No file reference in the catalog: the track exists, the audio does
        // not. Say so instead of pretending.
        setStatus("unavailable");
        setCurrentTime(0);
        setDuration(Number(track.duration_s) || 0);
        return;
      }
      setStatus("loading");
      setCurrentTime(0);
      setDuration(Number(track.duration_s) || 0);
      el.src = url;
      safeLoad(el);
      if (autoplay) safePlay(el);
    },
    [basePath],
  );

  // ── transport ──
  const play = useCallback(() => {
    const el = audioRef.current;
    if (!el || !currentTrack) return;
    safePlay(el);
    setPlaying(true);
  }, [currentTrack]);

  const pause = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    try {
      el.pause();
    } catch {
      /* not implemented in jsdom */
    }
    setPlaying(false);
  }, []);

  const toggle = useCallback(() => {
    if (playing) pause();
    else play();
  }, [playing, play, pause]);

  const step = useCallback(
    (delta) => {
      setIndex((prev) => {
        const nextIdx = prev + delta;
        if (nextIdx < 0 || nextIdx >= queue.length) return prev;
        return nextIdx;
      });
    },
    [queue.length],
  );

  const next = useCallback(() => step(1), [step]);
  const prev = useCallback(() => step(-1), [step]);

  // The track that just changed loads into the element. The autoplay flag
  // keeps the transport state: skipping while playing keeps playing; choosing
  // a track while paused loads it silently.
  useEffect(() => {
    if (!currentTrack) return;
    loadTrack(currentTrack, playing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack?.id, currentTrack?.file, basePath]);

  // ── the element's own events ──
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    const onLoaded = () => {
      const d = Number(el.duration);
      setDuration(Number.isFinite(d) && d > 0 ? d : Number(currentTrack?.duration_s) || 0);
      setStatus("ready");
    };
    const onTime = () => setCurrentTime(Number(el.currentTime) || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => nextRef.current();
    const onError = () => {
      // The audio could not be reached. The catalog is still true; the FILE
      // is not here. The UI shows the local-only state.
      setStatus("unavailable");
      setPlaying(false);
    };
    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("durationchange", onLoaded);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    el.addEventListener("error", onError);
    return () => {
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("durationchange", onLoaded);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("error", onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack?.id]);

  // ── queue control ──
  const playTrack = useCallback((track, tracks) => {
    const list = Array.isArray(tracks) ? tracks : [];
    const at = list.findIndex((t) => t && t.id === track?.id);
    if (at >= 0) {
      setQueue(list);
      setIndex(at);
    } else {
      setQueue([track]);
      setIndex(0);
    }
    setPlaying(true);
  }, []);

  const playPlaylist = useCallback((tracks, startIndex = 0) => {
    const list = (Array.isArray(tracks) ? tracks : []).filter(Boolean);
    if (!list.length) return;
    setQueue(list);
    setIndex(Math.max(0, Math.min(startIndex, list.length - 1)));
    setPlaying(true);
  }, []);

  const seek = useCallback((seconds) => {
    const el = audioRef.current;
    if (!el) return;
    const target = Math.max(0, Number(seconds) || 0);
    try {
      el.currentTime = target;
    } catch {
      /* not implemented in jsdom */
    }
    setCurrentTime(target);
  }, []);

  const seekBy = useCallback(
    (delta) => {
      seek(currentTime + (Number(delta) || 0));
    },
    [currentTime, seek],
  );

  const setVolume = useCallback((value) => {
    const v = Math.max(0, Math.min(1, Number(value) || 0));
    setVolumeState(v);
    const el = audioRef.current;
    if (el) {
      try {
        el.volume = v;
      } catch {
        /* not implemented in jsdom */
      }
    }
  }, []);

  const toggleQueue = useCallback(() => setQueueOpen((o) => !o), []);

  useEffect(() => {
    const el = audioRef.current;
    if (el) {
      try {
        el.volume = volume;
      } catch {
        /* not implemented in jsdom */
      }
    }
  }, [volume]);

  // Keep the transport handlers pointed at the current implementation.
  useEffect(() => {
    nextRef.current = next;
    prevRef.current = prev;
    playRef.current = play;
    pauseRef.current = pause;
  }, [next, prev, play, pause]);

  // ── MediaSession: the lockscreen / keyboard media keys ──
  //
  // The metadata is read from the track's own fields; the handlers call the
  // transport through refs, so the effect never goes stale.
  const metaTitle = currentTrack?.title || "";
  const metaArtist = currentTrack?.artist || "Kit";
  const metaAlbum = currentTrack?.album || "AgenticOS";

  useEffect(() => {
    const ms =
      typeof navigator !== "undefined" ? navigator.mediaSession : undefined;
    if (!ms) return undefined;
    try {
      if (typeof MediaMetadata !== "undefined" && metaTitle) {
        ms.metadata = new MediaMetadata({
          title: metaTitle,
          artist: metaArtist,
          album: metaAlbum,
        });
      } else {
        ms.metadata = null;
      }
    } catch {
      /* MediaMetadata is not constructible everywhere */
    }
    const handlers = {
      play: () => playRef.current(),
      pause: () => pauseRef.current(),
      previoustrack: () => prevRef.current(),
      nexttrack: () => nextRef.current(),
      seekbackward: (d) => seekRef.current(-((d && d.seekOffset) || 10)),
      seekforward: (d) => seekRef.current((d && d.seekOffset) || 10),
      stop: () => {
        seekRef.current(0);
        pauseRef.current();
      },
    };
    for (const action of Object.keys(handlers)) {
      try {
        ms.setActionHandler(action, handlers[action]);
      } catch {
        /* an unsupported action is not a failure */
      }
    }
    return () => {
      for (const action of Object.keys(handlers)) {
        try {
          ms.setActionHandler(action, null);
        } catch {
          /* ignore */
        }
      }
    };
  }, [metaTitle, metaArtist, metaAlbum]);

  const seekRef = useRef(() => {});
  useEffect(() => {
    seekRef.current = seekBy;
  }, [seekBy]);

  const value = useMemo(
    () => ({
      available: true,
      queue,
      index,
      currentTrack,
      playing,
      currentTime,
      duration,
      volume,
      status,
      isLocalOnly: status === "unavailable",
      queueOpen,
      playTrack,
      playPlaylist,
      toggle,
      play,
      pause,
      next,
      prev,
      seek,
      seekBy,
      setVolume,
      toggleQueue,
    }),
    [
      queue,
      index,
      currentTrack,
      playing,
      currentTime,
      duration,
      volume,
      status,
      queueOpen,
      playTrack,
      playPlaylist,
      toggle,
      play,
      pause,
      next,
      prev,
      seek,
      seekBy,
      setVolume,
      toggleQueue,
    ],
  );

  return (
    <MusicPlayerContext.Provider value={value}>
      {children}
      {/* The audio element is in the shell, above the routing: navigating
          between screens never stops the music. */}
      <audio
        ref={audioRef}
        data-testid="music-audio"
        preload="none"
        className="hidden"
      />
    </MusicPlayerContext.Provider>
  );
};

export default MusicPlayerProvider;
